import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as cartStyles from 'src/components/Listings/InventoryEventPage/MarketOrders/MarketOrdersCart.css';
import { useMarketOrdersCartContext } from 'src/components/Listings/InventoryEventPage/MarketOrders/MarketOrdersCartContext';
import {
  Content,
  useContent,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { Button } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { BulkListingPriceCalculation, Seating } from 'src/WebApiController';

import {
  ComparableListingDisplay,
  ComparableListingDisplayProps,
} from '../../ComparableListingDisplay';
import { ComparableListing } from '../ComparableListings';

export type ConnectedComparableListingDisplayProps = Pick<
  ComparableListingDisplayProps,
  'isCurrentListing'
> & {
  listing: ComparableListing;
  listingPriceCalculations?: BulkListingPriceCalculation[] | null;
  comparableListings?: ComparableListing[] | null;
  isExcludingFanInventory?: boolean;
};

export const ConnectedComparableListingDisplay: React.FC<
  ConnectedComparableListingDisplayProps
> = ({
  listing,
  listingPriceCalculations,
  comparableListings,
  isExcludingFanInventory,
  ...props
}) => {
  const {
    listingId,
    section,
    row,
    price,
    unitCost,
    currencyCode,
    isOutlier,
    isAnchor,
    isYourListing,
    isListed,
    isFanInventory,
    isSelectedWithListingGroup,
    validPurchaseQuantities,
    listingNotes,
    autoPricingResultType,
    autoPricingResultConstraintType,
    shouldShowResultTypeAction,
  } = listing;

  const cartContext = useMarketOrdersCartContext();
  const isInCart = cartContext?.cartItems.some(
    (item) => item.listingId === listingId
  );
  const [animated, setAnimated] = useState(false);
  const prevInCartRef = useRef(isInCart);

  useEffect(() => {
    if (!prevInCartRef.current && isInCart) {
      setAnimated(true);
      const timer = setTimeout(() => setAnimated(false), 450);
      prevInCartRef.current = true;
      return () => clearTimeout(timer);
    }
    if (!isInCart) {
      prevInCartRef.current = false;
    }
  }, [isInCart]);

  const handleAddToCart = useCallback(() => {
    cartContext?.addToCart(listing);
  }, [cartContext, listing]);

  const addToCartLabel = useContent(ContentId.AddToCart);
  const addSeatingToCartAriaLabel = useFormattedContent(
    FormatContentId.AddSeatingToCart,
    [section ?? '', row ?? '']
  ) as string;

  if (price == null || currencyCode == null) {
    return null;
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
  });

  const cartButton =
    cartContext != null ? (
      isInCart ? (
        <span className={cartStyles.inCartBadge}>
          &#10003; <Content id={ContentId.InCart} />
        </span>
      ) : (
        <Button
          className={cartStyles.addToCartBtn}
          variant="textPlain"
          shape="none"
          size="unset"
          onClick={handleAddToCart}
          type="button"
          aria-label={addSeatingToCartAriaLabel}
        >
          {addToCartLabel}
        </Button>
      )
    ) : null;

  return (
    <div
      className={clsx({
        [cartStyles.listingAddedAnimation]: animated,
        [cartStyles.listingInCart]: isInCart,
      })}
    >
      <ComparableListingDisplay
        {...props}
        listingId={listingId}
        isOutlier={isOutlier}
        isAnchor={isAnchor}
        isYourListing={isYourListing}
        isListed={isListed}
        isFanInventory={isFanInventory}
        isSelectedWithListingGroup={isSelectedWithListingGroup}
        price={
          cartButton != null ? (
            <span className={cartStyles.priceWithCartButton}>
              {formatter.format(price)}
              {cartButton}
            </span>
          ) : (
            formatter.format(price)
          )
        }
        unitCost={unitCost ? formatter.format(unitCost) : undefined}
        validPurchaseQuantities={validPurchaseQuantities}
        listingNotes={listingNotes}
        listingPriceCalculations={listingPriceCalculations}
        comparableListings={comparableListings}
        seating={
          {
            section: section ?? '',
            row: row ?? '',
          } as Seating
        }
        isExcludingFanInventory={isExcludingFanInventory}
        autoPricingResultType={autoPricingResultType}
        autoPricingResultConstraintType={autoPricingResultConstraintType}
        shouldShowResultTypeAction={shouldShowResultTypeAction}
      />
    </div>
  );
};
